// MA-C12 — `division_sharing` concept visual for fair-sharing partition.
//
// Shows `a` EMPTY rings (groups) and a pile of `total` (= a*b) items below.
// The child sees: "N groups (empty), M items to share" — must divide M by N.
//
// ANSWER-SAFE by construction:
//   • Rings are EMPTY — no ring ever shows the per-group count (the answer).
//   • The pile has `total = a*b` items — counting gives the DIVIDEND, not the answer.
//   • Counting rings gives `a` (number of groups, NOT the per-group answer).
//   • The child must perform the division mentally to find items-per-group.
//
// ADDITIVE — registers itself under the "division_sharing" kind (see bottom of
// file). The mapper (contentToEngineItem.ts) emits this kind ONLY for the
// "how many per group?" sub-type (expectedAnswer === b). The 3 "how many
// groups?" items (expectedAnswer === a) stay in NO_VISUAL (mapper returns
// undefined for those). No edit to MathVisual or the session screens.
//
// PROPS (MathRendererProps contract):
//   a     — number of groups (rings to draw); clamped to MAX_GROUPS
//   b     — per-group count (the answer); NOT rendered directly
//   total — dividend = a*b; the pile has this many items; clamped to MAX_TOTAL
//   op    — unused
import type { MathRendererProps } from "./rendererRegistry";

const DOT_PILE   = "var(--pal-orange, #d97742)";
const RING_STROKE = "var(--color-border-secondary, #cdbfae)";

// MA-708 — exported so the mapper (contentToEngineItem.ts) can gate
// EMISSION on these same numbers (mirroring MA-659's equal_groups →
// discrete_clusters gate) instead of duplicating them: importing the
// renderer's own clamp means mapper and renderer cannot drift apart the
// way they did for MEG_M19_008 (authored total=36 silently drawn as 30).
export const MAX_GROUPS = 6;   // maximum rings rendered
export const MAX_TOTAL  = 30;  // maximum pile items rendered

// Ring dimensions
const R_X  = 30;  // ring ellipse x-radius
const R_Y  = 26;  // ring ellipse y-radius (slightly shorter — oval feel)

// Ring layout: up to 3 per row
const RING_COLS     = 3;
const RING_PITCH_X  = 74;  // center-to-center horizontal
const RING_PITCH_Y  = 66;  // center-to-center vertical (for overflow rows)
const RING_PAD_X    = 38;  // first ring center x
const RING_PAD_Y    = 30;  // first ring center y

// Pile layout: 5 dots per row (mirrors the Dots() pattern in rendererRegistry)
const DOT_R         = 10;  // pile dot radius
const DOT_PITCH     = 26;  // dot center-to-center pitch
const DOT_COLS      = 5;
const DOT_PAD       = 13;  // first dot center offset from the pile's left edge

// Gap between the bottom of the rings section and the top of the pile
const GAP = 18;

export default function DivisionSharingRenderer({ a, b, total }: MathRendererProps) {
  const safeA = Math.max(
    1,
    Math.min(MAX_GROUPS, Math.floor(Number.isFinite(a ?? NaN) ? (a as number) : 2)),
  );
  // `b` is the answer — kept for the aria-label; not depicted in the visual.
  const safeB = Number.isFinite(b ?? NaN) ? Math.max(1, Math.round(b as number)) : 2;
  const safeTotal = Math.max(
    0,
    Math.min(MAX_TOTAL, Math.round(Number.isFinite(total) ? total : safeA * safeB)),
  );

  // ── Ring layout ──────────────────────────────────────────────────────────
  const ringRows = Math.ceil(safeA / RING_COLS);
  const colsUsed = Math.min(safeA, RING_COLS);
  // Width needed for the ring section
  const ringsSectionW = RING_PAD_X * 2 + (colsUsed - 1) * RING_PITCH_X;
  // Height from top to the bottom edge of the last ring row
  const ringsSectionH = RING_PAD_Y + (ringRows - 1) * RING_PITCH_Y + R_Y;

  const rings = Array.from({ length: safeA }).map((_, i) => ({
    cx: RING_PAD_X + (i % RING_COLS) * RING_PITCH_X,
    cy: RING_PAD_Y + Math.floor(i / RING_COLS) * RING_PITCH_Y,
  }));

  // ── Pile layout ──────────────────────────────────────────────────────────
  const pileRows = Math.ceil(safeTotal / DOT_COLS);
  const pileCols = Math.min(safeTotal || 1, DOT_COLS);
  const pileSectionW = DOT_PAD * 2 + (pileCols - 1) * DOT_PITCH;
  const pileSectionH = pileRows > 0 ? DOT_PAD + (pileRows - 1) * DOT_PITCH : 0;

  // Pile starts below the rings
  const pileOffsetY = ringsSectionH + GAP;

  // ── Overall SVG dimensions ───────────────────────────────────────────────
  const svgWidth  = Math.max(ringsSectionW, pileSectionW);
  const svgHeight = pileOffsetY + pileSectionH + DOT_PAD;

  // Center the narrower section horizontally
  const ringsOffsetX  = Math.round((svgWidth - ringsSectionW) / 2);
  const pileOffsetX   = Math.round((svgWidth - pileSectionW) / 2);

  return (
    <svg
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      width="100%"
      style={{ maxWidth: Math.min(280, svgWidth) }}
      role="img"
      aria-label={`${safeA} groups, ${safeTotal} items to share equally`}
    >
      {/* Empty rings — the groups to share INTO */}
      {rings.map(({ cx, cy }, i) => (
        <ellipse
          key={`ring-${i}`}
          cx={cx + ringsOffsetX}
          cy={cy}
          rx={R_X}
          ry={R_Y}
          fill="none"
          stroke={RING_STROKE}
          strokeWidth="2"
        />
      ))}

      {/* Pile — the items to be shared */}
      {Array.from({ length: safeTotal }).map((_, i) => {
        const col = i % DOT_COLS;
        const row = Math.floor(i / DOT_COLS);
        const cx = pileOffsetX + DOT_PAD + col * DOT_PITCH;
        const cy = pileOffsetY + DOT_PAD + row * DOT_PITCH;
        return <circle key={`pile-${i}`} cx={cx} cy={cy} r={DOT_R} fill={DOT_PILE} />;
      })}
    </svg>
  );
}
